import {
  Button,
  CheckInput,
  DateInput,
  Icon,
  addToast,
  Modal,
  Table,
  ColumnTable,
} from '@octano/global-ui';
import { useState, useMemo, useCallback } from 'react';
import { Row, Col } from 'reactstrap';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { useTranslation } from 'react-i18next';
import { updateAdmissionProcessStagesAction } from './actions';

dayjs.extend(customParseFormat);

interface Stage {
  id: number;
  name: string;
  startDate?: string;
  endDate?: string;
  requiresDate?: boolean;
}

interface Props {
  stages: Stage[];
  processId: number;
}

export default function AdmissionProcessStagesEditor({
  stages,
  processId,
}: Props) {
  const { t } = useTranslation('academic-offer/admission-processes');
  const [stagesState, setStagesState] = useState(
    stages.map((stage) => ({
      ...stage,
      canChangeDate: stage.requiresDate,
      startDate: stage.startDate || '',
      endDate: stage.endDate || '',
    })),
  );
  const [showModal, setShowModal] = useState(false);
  const toggleModal = () => setShowModal(!showModal);

  const handleStageChange = useCallback(
    (id: number, field: keyof typeof stagesState[0], value: any) => {
      setStagesState((prev) => {
        const index = prev.findIndex((s) => s.id === id);
        if (index === -1) return prev;
        const newStages = [...prev];
        newStages[index] = { ...newStages[index], [field]: value };
        return newStages;
      });
    },
    [],
  );

  const handleSave = () => {
    toggleModal();
  };

  const confirmSave = async () => {
    toggleModal();
    const result = await updateAdmissionProcessStagesAction({
      processId,
      stages: stagesState.map((s) => ({
        id: s.id,
        startDate:
          s.requiresDate && s.startDate
            ? dayjs(s.startDate).format('DD/MM/YY')
            : null,
        endDate:
          s.requiresDate && s.endDate
            ? dayjs(s.endDate).format('DD/MM/YY')
            : null,
      })),
    });

    if (!result.success) {
      console.error(result.data);
      addToast({
        icon: 'error',
        text: t('stagesEditor.toasts.error'),
        color: 'danger',
      });
      return;
    }

    addToast({
      icon: 'success',
      text: t('stagesEditor.toasts.success'),
      color: 'success',
    });
  };

  const columns = useMemo<ColumnTable<any>[]>(
    () => [
      {
        headerText: t('stagesEditor.stageName'),
        columnName: 'name',
        thClassName: 'text-left pl-5',
        tdClassName: 'text-left pl-5 align-middle',
      },
      {
        headerText: t('stagesEditor.requiresDate'),
        columnName: 'requiresDate',
        thClassName: 'text-center',
        tdClassName: 'text-center align-middle',
        cellFormat: ({ row }: any) => (
          <div className="d-flex justify-content-center">
            <CheckInput
              name={`requiresDate-${row.id}`}
              value={row.requiresDate}
              label=""
              disabled={!row.canChangeDate}
              onChange={() =>
                handleStageChange(row.id, 'requiresDate', !row.requiresDate)
              }
            />
          </div>
        ),
      },
      {
        headerText: t('stagesEditor.startDate'),
        columnName: 'startDate',
        cellFormat: ({ row }: any) =>
          row.requiresDate ? (
            <DateInput
              name={`startDate-${row.id}`}
              label=""
              value={row.startDate || ''}
              onChange={(value: any) =>
                handleStageChange(row.id, 'startDate', value)
              }
            />
          ) : (
            <div className="text-center">
              <span className="text-muted">
                {t('stagesEditor.notApplicable')}
              </span>
            </div>
          ),
      },
      {
        headerText: t('stagesEditor.endDate'),
        columnName: 'endDate',
        cellFormat: ({ row }: any) =>
          row.requiresDate ? (
            <DateInput
              name={`endDate-${row.id}`}
              label=""
              value={row.endDate || ''}
              onChange={(value: any) =>
                handleStageChange(row.id, 'endDate', value)
              }
            />
          ) : (
            <div className="text-center">
              <span className="text-muted">
                {t('stagesEditor.notApplicable')}
              </span>
            </div>
          ),
      },
    ],
    [t, handleStageChange],
  );

  if (!stages || stages.length === 0) {
    return <div className="text-center p-2">{t('stagesEditor.noStages')}</div>;
  }

  return (
    <div>
      <div className="fs-18 font-weight-bold text-primary text-uppercase">
        {t('management.stages')}
      </div>
      <p className="fs-16 mb-4">{t('management.stagesDescription')}</p>
      <div
        id="admission-process-stages-table"
        className="mb-3 bg-white border-primary"
      >
        <Table columns={columns} data={stagesState} />
      </div>
      <div className="d-flex justify-content-end">
        <Button
          size="md"
          className="px-5 font-weight-bold mt-3"
          text={t('stagesEditor.saveChanges')}
          onClick={handleSave}
        />
      </div>

      <Modal isOpen={showModal} toggle={toggleModal} size="md" unmountOnClose>
        <Row className="mt-4 justify-content-center">
          <Col md={10}>
            <div className="text-center">
              <Icon color="secondary" name="warning" size="70px" />
              <h4 className="mt-3">{t('stagesEditor.modal.title')}</h4>
            </div>
          </Col>
        </Row>
        <Row className="mt-5 mb-3">
          <Col xs={{ size: 12, order: 2 }} md={{ size: 6, order: 1 }}>
            <Button
              type="button"
              text={t('stagesEditor.modal.cancel')}
              outlined
              onClick={toggleModal}
              fullwidth
            />
          </Col>
          <Col xs={{ size: 12, order: 1 }} md={{ size: 6, order: 2 }}>
            <Button
              type="button"
              onClick={confirmSave}
              text={t('stagesEditor.modal.confirm')}
              fullwidth
              className="mb-3 mb-md-0"
            />
          </Col>
        </Row>
      </Modal>
    </div>
  );
}
